import { useState, useEffect } from "react"
import axios from "axios"
import useLocalStorageState from "use-local-storage-state"
import YouTube from "react-youtube"
import CategoryRow from "./CategoryRow"
import FavoritesRow from "./FavoritesRow"
import Footer from "./Footer"

const IMG_BASE_BACKDROP = "https://image.tmdb.org/t/p/original"
const IMG_BASE_PROVIDER = "https://image.tmdb.org/t/p/w92"
const API_KEY = process.env.REACT_APP_TMDB_API_KEY

export default function MovieView({ movie, onBack, onMovieClick, popularMovies }) {
  const [clips, setClips] = useState([])
  const [currentClip, setCurrentClip] = useState(null)
  const [providers, setProviders] = useState([])
  const [playing, setPlaying] = useState(false)
  const [likedMovies, setLikedMovies] = useLocalStorageState("likedMovies", { defaultValue: [] })

  const isLiked = likedMovies.some((m) => m.id === movie.id)

  const toggleLike = () => {
    if (isLiked) {
      setLikedMovies((prev) => prev.filter((m) => m.id !== movie.id))
    } else {
      setLikedMovies((prev) => [...prev, movie])
    }
  }

  useEffect(() => {
    setPlaying(false)
    setClips([])
    setCurrentClip(null)
    setProviders([])

    // Fetch clips
    axios
      .get(`https://api.themoviedb.org/3/movie/${movie.id}/videos?api_key=${API_KEY}`)
      .then((res) => {
        const videos = res.data.results
        const clips = videos.filter((v) => v.type === "Clip")
        const trailers = videos.filter((v) => v.type === "Trailer")
        const teasers = videos.filter((v) => v.type === "Teaser")
        const list = clips.length > 0 ? clips : trailers.length > 0 ? trailers : teasers
        const reversed = [...list].reverse()
        setClips(reversed)
        setCurrentClip(reversed[0] || videos[videos.length - 1] || null)
      })
      .catch(() => {})

    // Fetch providers
    axios
      .get(`https://api.themoviedb.org/3/movie/${movie.id}/watch/providers?api_key=${API_KEY}`)
      .then((res) => {
        const mx = res.data.results?.MX
        if (!mx) return
        const combined = [
          ...(mx.flatrate || []),
          ...(mx.rent || []),
          ...(mx.buy || []),
        ]
        const seen = new Set()
        const deduped = combined
          .filter((p) => {
            if (seen.has(p.provider_id)) return false
            seen.add(p.provider_id)
            return true
          })
          .sort((a, b) => a.display_priority - b.display_priority)
        setProviders(deduped)
      })
      .catch(() => {})
  }, [movie.id])

  const backdropUrl = movie.backdrop_path
    ? `${IMG_BASE_BACKDROP}${movie.backdrop_path}`
    : null

  const playerOpts = {
    width: "100%",
    height: "100%",
    playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
  }

  const belowMovies = likedMovies.length > 0
    ? [...likedMovies].reverse()
    : popularMovies

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hero backdrop */}
      <div className="relative w-full aspect-video max-h-[70vh] overflow-hidden">
        {playing && currentClip ? (
          <div className="w-full h-full bg-black">
            <YouTube
              videoId={currentClip.key}
              opts={playerOpts}
              className="w-full h-full"
              iframeClassName="w-full h-full"
            />
          </div>
        ) : (
          <>
            {backdropUrl && (
              <img
                src={backdropUrl}
                alt={movie.title}
                className="w-full h-full object-cover object-top"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />

            {/* Play button */}
            {currentClip && (
              <button
                onClick={() => setPlaying(true)}
                className="absolute inset-0 flex items-center justify-center group"
              >
                <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm group-hover:bg-white/30 transition-colors flex items-center justify-center border-2 border-white/50">
                  <svg className="w-12 h-12 text-white ml-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </button>
            )}
          </>
        )}
      </div>

      {/* Content */}
      <div className="px-6 md:px-10 py-6 max-w-5xl">
        {/* Back */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-yellow-400 hover:text-yellow-300 transition-colors mb-4"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Title + like */}
        <div className="flex items-start gap-3 mb-2">
          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            {movie.title}
          </h1>
          <button onClick={toggleLike} className="mt-1 shrink-0">
            <svg
              className={`w-7 h-7 transition-colors ${isLiked ? "text-yellow-400 fill-yellow-400" : "text-zinc-500 hover:text-yellow-400"}`}
              fill={isLiked ? "currentColor" : "none"}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 text-sm text-zinc-400 mb-4">
          {movie.release_date && (
            <span>{movie.release_date.split("-")[0]}</span>
          )}
          {movie.vote_average > 0 && (
            <span className="flex items-center gap-1 text-yellow-400 font-semibold">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              {movie.vote_average.toFixed(1)}
            </span>
          )}
        </div>

        {/* Overview */}
        <p className="text-zinc-300 text-sm leading-relaxed max-w-2xl mb-6">
          {movie.overview || "No description available."}
        </p>

        {/* Clips selector */}
        {clips.length > 1 && (
          <div className="flex gap-2 flex-wrap mb-6">
            {clips.map((clip) => (
              <button
                key={clip.id}
                onClick={() => { setCurrentClip(clip); setPlaying(true) }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors truncate max-w-[180px] ${
                  currentClip?.id === clip.id
                    ? "bg-yellow-400 text-zinc-900"
                    : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                }`}
              >
                {clip.name}
              </button>
            ))}
          </div>
        )}

        {/* Providers */}
        {providers.length > 0 && (
          <div className="mb-6">
            <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2 font-semibold">
              Available on
            </p>
            <div className="flex gap-2 flex-wrap">
              {providers.map((p) => (
                <img
                  key={p.provider_id}
                  src={`${IMG_BASE_PROVIDER}${p.logo_path}`}
                  alt={p.provider_name}
                  title={p.provider_name}
                  className="w-9 h-9 rounded-lg object-cover"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Below: favoritos o popular */}
      {likedMovies.length > 0 ? (
        <FavoritesRow
          movies={[...likedMovies].reverse()}
          onMovieClick={onMovieClick}
          currentMovie={movie}
        />
      ) : (
        <CategoryRow
          label="You may also like"
          movies={popularMovies}
          onMovieClick={onMovieClick}
          currentMovie={movie}
        />
      )}

      <Footer />
    </div>
  )
}
